import { IContentService } from '@grubhubprod/chiri/dist/core/services/content-service/content-service';
import { ContentQuery } from '@grubhubprod/chiri/dist/core/models/content-query';
import { FeatureToggleContainer } from '../models/FeatureToggleContainer';
import { AnyToggleValue, ContentfulFeatureToggle } from '../types';
import { LocalStorageUtility } from '../../utilities/localStorage.utility';
import {
  CancellableTimeout,
  getCancellableTimeout,
} from '../../utilities/getCancellableTimeout';
import { appConfigFactory, windowFactory } from '../../factories';
import { getChiriContentService } from '../../utilities/getChiriContentService.utility';
import { FeatureToggleDefaults } from '../featureToggles';

export interface FeatureToggleService {
  fetchTogglesFromContentful(): Promise<void>;

  getToggleValue<T>(key: string, defaultValue?: T): T;
  hasLocalToggleValue(key: string): boolean;
  setLocalToggleValue(key: string, value: AnyToggleValue);
  removeLocalToggleValue(key: string): void;
  overridesDefaultValue(key: string): boolean;
}

export class FeatureToggleServiceImpl implements FeatureToggleService {
  private static readonly FEATURE_TOGGLE_TYPE = 'hybridFeatureToggle';
  private static readonly TOGGLES_STORAGE_KEY = 'hybrid-testingToggles';
  private static readonly FEATURE_TOGGLE_MAX_COUNT = 1000;
  private static readonly FETCH_FEATURE_TOGGLE_TIMEOUT = 3000;

  private static instance: FeatureToggleServiceImpl;

  public static getInstance(): FeatureToggleService {
    if (!FeatureToggleServiceImpl.instance) {
      const appConfig = appConfigFactory();
      FeatureToggleServiceImpl.instance = new FeatureToggleServiceImpl(
        getChiriContentService({
          config: appConfig,
          spaceId: appConfig?.contentfulFeatureToggleSpaceId,
          spaceAccessToken: appConfig?.contentfulFeatureToggleAccessToken,
        }),
        new LocalStorageUtility(),
        windowFactory(),
        appConfig,
      );
    }

    return FeatureToggleServiceImpl.instance;
  }

  private featureToggleContainer: FeatureToggleContainer =
    new FeatureToggleContainer();
  private cancellableTimeout: CancellableTimeout = null;

  constructor(
    private readonly contentService: IContentService,
    private readonly storage: LocalStorageUtility,
    private readonly windowInstance: Window,
    private readonly appConfig: AppConfig,
  ) {}

  public async fetchTogglesFromContentful(): Promise<void> {
    this.cancellableTimeout?.cancel();

    const promise = new Promise<{ items: ContentfulFeatureToggle<any>[] }>(
      (resolve, reject) => {
        const query = this.getContentQuery();
        this.contentService
          .getContentEntry(query)
          .then((response) => resolve(response as any))
          .catch(reject);

        this.cancellableTimeout = this.getCancellableTimeout(resolve, reject);
        this.cancellableTimeout.start();
      },
    );

    const data = await promise;

    this.cancellableTimeout?.cancel();
    this.featureToggleContainer = new FeatureToggleContainer(data?.items);
  }

  private getCancellableTimeout(
    resolve: Function,
    reject: Function,
  ): CancellableTimeout {
    return getCancellableTimeout({
      callback: () => reject(new Error('Feature Toggle fetch timed out')),
      cancelCallback: resolve,
      ms: FeatureToggleServiceImpl.FETCH_FEATURE_TOGGLE_TIMEOUT,
    });
  }

  private getContentQuery(): ContentQuery {
    return new ContentQuery(
      {
        page: 0,
        contentType: FeatureToggleServiceImpl.FEATURE_TOGGLE_TYPE,
        hybridFeatureToggle_enabled: true,
      },
      {
        maxCount: FeatureToggleServiceImpl.FEATURE_TOGGLE_MAX_COUNT,
        hasUnpublish: false,
        publishDate: '',
        orderBy: '',
        include: 10,
      },
    );
  }

  public overridesDefaultValue(key: string): boolean {
    return this.featureToggleContainer.overridesDefaultValue(key);
  }

  public getToggleValue<T>(key: string, defaultValue?: T): T {
    let rv: T;

    if (this.hasLocalToggleValue(key)) {
      rv = this.getLocalToggleValue<T>(key);
    } else if (this.overridesDefaultValue(key)) {
      rv = this.featureToggleContainer.getValue<T>(key);
    } else if (this.hasVariantSet(key)) {
      rv = this.getVariantValue<T>(key);
    }

    return rv ?? FeatureToggleDefaults[key] ?? defaultValue;
  }

  private getLocalToggleValue<T>(key: string): T {
    return this.storedToggles[key].value;
  }

  private getVariants(): Record<string, any> {
    return this.windowInstance.variants ?? {};
  }

  private hasVariantSet(key: string): boolean {
    return !!this.getVariants()[key];
  }

  private getVariantValue<T>(key: string): T {
    return this.getVariants()[key].value;
  }

  private get storedToggles(): { [key: string]: AnyToggleValue } {
    return this.storage.get(FeatureToggleServiceImpl.TOGGLES_STORAGE_KEY) ?? {};
  }

  public hasLocalToggleValue(key: string): boolean {
    return !!this.storedToggles[key];
  }

  public setLocalToggleValue(key: string, value: AnyToggleValue): void {
    this.storage.set(FeatureToggleServiceImpl.TOGGLES_STORAGE_KEY, {
      ...this.storedToggles,
      [key]: value,
    });
  }

  public removeLocalToggleValue(key: string): void {
    const storedToggles = this.storedToggles;

    delete storedToggles[key];

    this.storage.set(
      FeatureToggleServiceImpl.TOGGLES_STORAGE_KEY,
      storedToggles,
    );
  }
}
